import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const http = axios.create({ baseURL: API, timeout: 30000, withCredentials: true });

// Auth: on 401 try one silent refresh, then hard-redirect to /login.
let refreshing = null;
http.interceptors.response.use(
  (r) => r,
  async (error) => {
    const { response, config } = error;
    if (response?.status === 401 && !config._retried && !config.url.includes("/auth/")) {
      config._retried = true;
      try {
        refreshing = refreshing || axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        await refreshing;
        refreshing = null;
        return http(config);
      } catch {
        refreshing = null;
        if (window.location.pathname !== "/login") window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  },
);

export const authApi = {
  login: (email, password) =>
    http.post(`/auth/login`, { email, password }).then((r) => r.data),
  logout: () => http.post(`/auth/logout`).then((r) => r.data),
  me: () => http.get(`/auth/me`).then((r) => r.data),
};

export const popsApi = {
  bootstrap: () => http.get(`/partnerops/bootstrap`).then((r) => r.data),
  ledger: (period, customerId) =>
    http.get(`/partnerops/ledger?period=${period}${customerId ? `&customer_id=${customerId}` : ""}`).then((r) => r.data),
  runInvoices: (period, customerId) =>
    http.post(`/partnerops/invoices/run`, { period, customer_id: customerId || null }).then((r) => r.data),
  invoices: (period, state) => {
    const p = new URLSearchParams();
    if (period) p.append("period", period);
    if (state) p.append("state", state);
    return http.get(`/partnerops/invoices?${p}`).then((r) => r.data);
  },
  invoice: (id) => http.get(`/partnerops/invoices/${id}`).then((r) => r.data),
  transition: (id, action, actor = "dashboard-user", reason = "") =>
    http.post(`/partnerops/invoices/${id}/transition`, { action, actor, reason }).then((r) => r.data),
  creditNote: (id, reason, amount) =>
    http.post(`/partnerops/invoices/${id}/credit-note`, { reason, amount }).then((r) => r.data),
  exportUrl: (id, format) => `${API}/partnerops/invoices/${id}/export?format=${format}`,
  addCharge: (payload) => http.post(`/partnerops/charges`, payload).then((r) => r.data),
  deleteCharge: (id) => http.delete(`/partnerops/charges/${id}`).then((r) => r.data),
  suppressCharge: (id, scope, reason) =>
    http.post(`/partnerops/charges/${id}/suppress`, { scope, reason }).then((r) => r.data),
  setSeats: (id, seats) => http.patch(`/partnerops/csp/${id}/seats`, { seats }).then((r) => r.data),
  setPriceBook: (customerId, priceBookId) =>
    http.patch(`/partnerops/customers/${customerId}/price-book`, { price_book_id: priceBookId }).then((r) => r.data),
  runReport: (definition) => http.post(`/partnerops/reports/run`, definition).then((r) => r.data),
  nlReport: (prompt) => http.post(`/partnerops/reports/nl`, { prompt }).then((r) => r.data),
  views: () => http.get(`/partnerops/reports/views`).then((r) => r.data),
  saveView: (name, definition, schedule) =>
    http.post(`/partnerops/reports/views`, { name, definition, schedule }).then((r) => r.data),
  runView: (id) => http.post(`/partnerops/reports/views/${id}/run`).then((r) => r.data),
  deleteView: (id) => http.delete(`/partnerops/reports/views/${id}`).then((r) => r.data),
  profit: (period, groupBy) =>
    http.get(`/partnerops/analytics/profit?period=${period}&group_by=${groupBy}`).then((r) => r.data),
  waterfall: (period, customerId) =>
    http.get(`/partnerops/analytics/waterfall?period=${period}${customerId ? `&customer_id=${customerId}` : ""}`).then((r) => r.data),
  leakage: (period) => http.get(`/partnerops/analytics/leakage?period=${period}`).then((r) => r.data),
  hierarchy: (period) => http.get(`/partnerops/hierarchy?period=${period}`).then((r) => r.data),
  sendInvoice: (id, to) => http.post(`/partnerops/invoices/${id}/send`, { to: to || null }).then((r) => r.data),
  outbox: () => http.get(`/partnerops/outbox`).then((r) => r.data),
  outboxMsg: (id) => http.get(`/partnerops/outbox/${id}`).then((r) => r.data),
  restatements: () => http.get(`/partnerops/restatements`).then((r) => r.data),
  simulateRestatement: (body = {}) => http.post(`/partnerops/restatements/simulate`, body).then((r) => r.data),
  applyRestatement: (rid, cid) => http.post(`/partnerops/restatements/${rid}/proposals/${cid}/apply`, {}).then((r) => r.data),
  dismissRestatement: (rid, cid, reason = "") =>
    http.post(`/partnerops/restatements/${rid}/proposals/${cid}/dismiss`, { reason }).then((r) => r.data),
  portalUsers: () => http.get(`/partnerops/portal-users`).then((r) => r.data),
  createPortalUser: (customer_id, email, password) =>
    http.post(`/partnerops/portal-users`, { customer_id, email, password }).then((r) => r.data),
  resetPortalUser: (id, password) => http.post(`/partnerops/portal-users/${id}/reset`, { password }).then((r) => r.data),
  chargeModifier: () => http.get(`/partnerops/charge-modifier`).then((r) => r.data),
  setChargeModifier: (pct) => http.put(`/partnerops/charge-modifier`, { pct }).then((r) => r.data),
};

export const reportsApi = {
  catalog: () => http.get(`/reports/catalog`).then((r) => r.data),
  filters: () => http.get(`/reports/filters`).then((r) => r.data),
  v2Fields: (provider = "all") => http.get(`/reports/v2/fields`, { params: { provider } }).then((r) => r.data),
  v2Preview: (definition) => http.post(`/reports/v2/preview`, definition).then((r) => r.data),
  v2Export: (definition) => http.post(`/reports/v2/export`, definition, { responseType: "blob" }).then((r) => r.data),
  v2Saved: () => http.get(`/reports/v2/saved`).then((r) => r.data),
  v2Save: (body) => http.post(`/reports/v2/saved`, body).then((r) => r.data),
  v2RunSaved: (id) => http.get(`/reports/v2/saved/${id}/run`).then((r) => r.data),
  v2Delete: (id) => http.delete(`/reports/v2/saved/${id}`).then((r) => r.data),
  run: (id, params = {}) => http.get(`/reports/run/${id}`, { params }).then((r) => r.data),
  exportUrl: (id, params = {}) => {
    const p = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v != null)));
    return `${API}/reports/run/${id}/export?${p}`;
  },
  my: () => http.get(`/reports/my`).then((r) => r.data),
  save: (report_id, name, params = {}) => http.post(`/reports/my`, { report_id, name, params }).then((r) => r.data),
  deleteSaved: (id) => http.delete(`/reports/my/${id}`).then((r) => r.data),
  credits: () => http.get(`/reports/credits`).then((r) => r.data),
  assignCredit: (id, account_id) => http.post(`/reports/credits/${id}/assign`, { account_id }).then((r) => r.data),
  memos: () => http.get(`/reports/memos`).then((r) => r.data),
  addMemo: (memo) => http.post(`/reports/memos`, memo).then((r) => r.data),
  deleteMemo: (id) => http.delete(`/reports/memos/${id}`).then((r) => r.data),
  rates: () => http.get(`/reports/usage-rates`).then((r) => r.data),
  addRate: (rate) => http.post(`/reports/usage-rates`, rate).then((r) => r.data),
  deleteRate: (id) => http.delete(`/reports/usage-rates/${id}`).then((r) => r.data),
  families: () => http.get(`/reports/families`).then((r) => r.data),
  createFamily: (name, description = "") => http.post(`/reports/families`, { name, description }).then((r) => r.data),
  moveAccount: (account_id, family_id) => http.post(`/reports/families/move-account`, { account_id, family_id }).then((r) => r.data),
  jobs: () => http.get(`/reports/jobs`).then((r) => r.data),
  runJob: (type, detail = "") => http.post(`/reports/jobs`, { type, detail }).then((r) => r.data),
  customFields: () => http.get(`/reports/custom/fields`).then((r) => r.data),
  customPreview: (body) => http.post(`/reports/custom/preview`, body).then((r) => r.data),
  customList: () => http.get(`/reports/custom`).then((r) => r.data),
  customSave: (name, definition) => http.post(`/reports/custom`, { name, definition }).then((r) => r.data),
  customRun: (id) => http.get(`/reports/custom/${id}/run`).then((r) => r.data),
  customExportUrl: (id) => `${API}/reports/custom/${id}/export`,
  customDelete: (id) => http.delete(`/reports/custom/${id}`).then((r) => r.data),
};

// ── Customer portal (separate auth realm: portal_token cookie) ─────────────
const phttp = axios.create({ baseURL: API, timeout: 30000, withCredentials: true });
phttp.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401 && !error.config?.url?.includes("/portal/auth/")
        && window.location.pathname !== "/portal/login") {
      window.location.href = "/portal/login";
    }
    return Promise.reject(error);
  },
);

export const portalApi = {
  login: (email, password) => phttp.post(`/portal/auth/login`, { email, password }).then((r) => r.data),
  logout: () => phttp.post(`/portal/auth/logout`).then((r) => r.data),
  me: () => phttp.get(`/portal/auth/me`).then((r) => r.data),
  invoices: () => phttp.get(`/portal/invoices`).then((r) => r.data),
  invoice: (id) => phttp.get(`/portal/invoices/${id}`).then((r) => r.data),
  usage: (period) => phttp.get(`/portal/usage?period=${period}`).then((r) => r.data),
  exportUrl: (id) => `${API}/portal/invoices/${id}/export`,
  reportsCatalog: () => phttp.get(`/portal/reports/catalog`).then((r) => r.data),
  reportsRun: (id, months = 6) => phttp.get(`/portal/reports/run/${id}?months=${months}`).then((r) => r.data),
  reportsMy: () => phttp.get(`/portal/reports/my`).then((r) => r.data),
  reportsSave: (report_id, name) => phttp.post(`/portal/reports/my`, { report_id, name }).then((r) => r.data),
  reportsDeleteSaved: (id) => phttp.delete(`/portal/reports/my/${id}`).then((r) => r.data),
  customFields: () => phttp.get(`/portal/reports/custom/fields`).then((r) => r.data),
  customPreview: (body) => phttp.post(`/portal/reports/custom/preview`, body).then((r) => r.data),
  customList: () => phttp.get(`/portal/reports/custom`).then((r) => r.data),
  econ: {
    widgets: () => phttp.get(`/portal/econ/widgets`).then((r) => r.data),
    widgetData: () => phttp.get(`/portal/econ/widget-data`).then((r) => r.data),
    dashboards: () => phttp.get(`/portal/econ/dashboards`).then((r) => r.data),
    createDashboard: (payload) => phttp.post(`/portal/econ/dashboards`, payload).then((r) => r.data),
    updateDashboard: (id, payload) => phttp.put(`/portal/econ/dashboards/${id}`, payload).then((r) => r.data),
    deleteDashboard: (id) => phttp.delete(`/portal/econ/dashboards/${id}`).then((r) => r.data),
  },
  customSave: (name, definition) => phttp.post(`/portal/reports/custom`, { name, definition }).then((r) => r.data),
  customRun: (id) => phttp.get(`/portal/reports/custom/${id}/run`).then((r) => r.data),
  customDelete: (id) => phttp.delete(`/portal/reports/custom/${id}`).then((r) => r.data),
};

export const api = {
  health: () => http.get("/health").then((r) => r.data),
  status: () => http.get("/status").then((r) => r.data),
  config: () => http.get("/config").then((r) => r.data),
  triggerScan: (project_ids) =>
    http.post("/scan", { project_ids: project_ids || null }).then((r) => r.data),
  scans: (limit = 20) => http.get(`/scans?limit=${limit}`).then((r) => r.data),
  scan: (id) => http.get(`/scans/${id}`).then((r) => r.data),
  summary: () => http.get("/summary").then((r) => r.data),
  trends: (limit = 20) => http.get(`/trends?limit=${limit}`).then((r) => r.data),
  opportunities: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== "" && v !== "all") {
        params.append(k, v);
      }
    });
    return http.get(`/opportunities?${params.toString()}`).then((r) => r.data);
  },
  opportunity: (id) => http.get(`/opportunities/${id}`).then((r) => r.data),
  setLifecycle: (id, status) =>
    http.patch(`/opportunities/${id}/lifecycle`, { status }).then((r) => r.data),
  wastegraph: {
    summary: () => http.get(`/wastegraph/summary`).then((r) => r.data),
    commitments: () => http.get(`/wastegraph/commitments`).then((r) => r.data),
  },
  ledger: {
    summary: () => http.get(`/ledger/summary`).then((r) => r.data),
    entries: (status, limit = 200) => {
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      params.append("limit", limit);
      return http.get(`/ledger/entries?${params}`).then((r) => r.data);
    },
    reverse: (id, reason) =>
      http.post(`/ledger/${id}/reverse`, { reason }).then((r) => r.data),
  },
  autofix: {
    pkg: (oppId) => http.get(`/autofix/package/${oppId}`).then((r) => r.data),
    terraform: (oppId) => http.get(`/autofix/terraform/${oppId}`).then((r) => r.data),
    prompt: (oppId) => http.get(`/autofix/prompt/${oppId}`).then((r) => r.data),
    trustPolicy: () => http.get(`/autofix/trust-policy`).then((r) => r.data),
    setTrustPolicy: (levels) =>
      http.put(`/autofix/trust-policy`, { levels }).then((r) => r.data),
  },
  aiCost: {
    spend: (days = 30) => http.get(`/ai-cost/spend?days=${days}`).then((r) => r.data),
    idle: () => http.get(`/ai-cost/idle`).then((r) => r.data),
    simulate: (payload) => http.post(`/ai-cost/simulate`, payload).then((r) => r.data),
    policies: () => http.get(`/ai-cost/policies`).then((r) => r.data),
    addPolicy: (p) => http.post(`/ai-cost/policies`, p).then((r) => r.data),
    deletePolicy: (id) => http.delete(`/ai-cost/policies/${id}`).then((r) => r.data),
  },
  coworkers: {
    templates: () => http.get(`/coworkers/templates`).then((r) => r.data),
    list: () => http.get(`/coworkers`).then((r) => r.data),
    create: (description, name) =>
      http.post(`/coworkers`, { description, name }).then((r) => r.data),
    activate: (id) => http.post(`/coworkers/${id}/activate`).then((r) => r.data),
    pause: (id) => http.post(`/coworkers/${id}/pause`).then((r) => r.data),
    runNow: (id) => http.post(`/coworkers/${id}/run-now`).then((r) => r.data),
    remove: (id) => http.delete(`/coworkers/${id}`).then((r) => r.data),
  },
  tokens: {
    list: () => http.get(`/tokens`).then((r) => r.data),
    create: (name, scopes) => http.post(`/tokens`, { name, scopes }).then((r) => r.data),
    revoke: (id) => http.delete(`/tokens/${id}`).then((r) => r.data),
  },
  remediations: (status) => {
    const q = status && status !== "all" ? `?status=${status}` : "";
    return http.get(`/remediations${q}`).then((r) => r.data);
  },
  remediation: (id) => http.get(`/remediations/${id}`).then((r) => r.data),
  approveRemediation: (id, approved_by = "dashboard-user") =>
    http.post(`/remediations/${id}/approve`, { approved_by }).then((r) => r.data),
  rejectRemediation: (id, reason = "Rejected via dashboard") =>
    http.post(`/remediations/${id}/reject`, {
      rejected_by: "dashboard-user",
      reason,
    }).then((r) => r.data),
  executeRemediation: (id) =>
    http.post(`/remediations/${id}/execute`).then((r) => r.data),
  ue: {
    summary: (days = 30) => http.get(`/ue/summary?days=${days}`).then((r) => r.data),
    customers: (days = 30) => http.get(`/ue/customers?days=${days}`).then((r) => r.data),
    customer: (id, days = 30) => http.get(`/ue/customers/${id}?days=${days}`).then((r) => r.data),
    products: (days = 30) => http.get(`/ue/products?days=${days}`).then((r) => r.data),
    trends: (days = 30) => http.get(`/ue/trends?days=${days}`).then((r) => r.data),
    events: () => http.get(`/ue/events`).then((r) => r.data),
    metrics: () => http.get(`/ue/metrics`).then((r) => r.data),
    calculate: (payload) => http.post(`/ue/calculate`, payload).then((r) => r.data),
    hierarchy: (params = {}) => {
      const q = new URLSearchParams(params).toString();
      return http.get(`/ue/hierarchy?${q}`).then((r) => r.data);
    },
  },
  gov: {
    summary: () => http.get(`/gov/summary`).then((r) => r.data),
    coverageServices: () => http.get(`/gov/coverage/services`).then((r) => r.data),
    coverageTags: () => http.get(`/gov/coverage/tags`).then((r) => r.data),
    coverageTrend: (days = 30) => http.get(`/gov/coverage/trend?days=${days}`).then((r) => r.data),
    untaggedSpend: (limit = 25) => http.get(`/gov/untagged-spend?limit=${limit}`).then((r) => r.data),
    violations: (limit = 50) => http.get(`/gov/violations?limit=${limit}`).then((r) => r.data),
    rules: () => http.get(`/gov/rules`).then((r) => r.data),
    toggleRule: (id, enabled) => http.post(`/gov/rules/${id}/toggle`, { enabled }).then((r) => r.data),
    requiredTags: () => http.get(`/gov/required-tags`).then((r) => r.data),
  },
  providers: {
    list: () => http.get(`/providers`).then((r) => r.data),
    detail: (id) => http.get(`/providers/${id}`).then((r) => r.data),
    connect: (id, credentials) => http.post(`/providers/${id}/connect`, { credentials }).then((r) => r.data),
    validate: (id, credentials) => http.post(`/providers/${id}/validate`, { credentials }).then((r) => r.data),
    disconnect: (id) => http.post(`/providers/${id}/disconnect`).then((r) => r.data),
  },
  ingestion: {
    status: () => http.get(`/ingestion/status`).then((r) => r.data),
  },
  tm: {
    summary: (days = 30) => http.get(`/tm/summary?days=${days}`).then((r) => r.data),
    trends: (days = 30) => http.get(`/tm/trends?days=${days}`).then((r) => r.data),
    routing: (days = 30) => http.get(`/tm/routing?days=${days}`).then((r) => r.data),
    agents: (days = 30) => http.get(`/tm/agents?days=${days}`).then((r) => r.data),
    cache: (limit = 30) => http.get(`/tm/cache?limit=${limit}`).then((r) => r.data),
    incidents: (days = 7, limit = 20) => http.get(`/tm/incidents?days=${days}&limit=${limit}`).then((r) => r.data),
    breakdown: (days = 30) => http.get(`/tm/breakdown?days=${days}`).then((r) => r.data),
    providers: (days = 30) => http.get(`/tm/providers?days=${days}`).then((r) => r.data),
    catalog: () => http.get(`/tm/catalog`).then((r) => r.data),
    simulate: (body) => http.post(`/tm/simulate`, body).then((r) => r.data),
    advisor: (body) => http.post(`/tm/advisor`, body).then((r) => r.data),
    bestModels: {
      catalog: () => http.get(`/tm/best-models/catalog`).then((r) => r.data),
      analyze: (body) => http.post(`/tm/best-models/analyze`, body).then((r) => r.data),
      heatmap: (limit = 10) => http.get(`/tm/best-models/savings-heatmap?limit=${limit}`).then((r) => r.data),
      accuracy: () => http.get(`/tm/best-models/prediction-accuracy`).then((r) => r.data),
    },
  },
  tmPlayground: {
    chat: (session_id, message, mode = "auto") =>
      http.post(`/tm/playground/chat`, { session_id, message, mode }, { timeout: 60000 }).then((r) => r.data),
    cache: () => http.get(`/tm/playground/cache`).then((r) => r.data),
    clearCache: () => http.delete(`/tm/playground/cache`).then((r) => r.data),
    session: (id) => http.get(`/tm/playground/sessions/${id}`).then((r) => r.data),
  },
  dex: {
    status: () => http.get("/dex/status").then((r) => r.data),
    chat: (session_id, message) =>
      http.post("/dex/chat", { session_id, message }).then((r) => r.data),
    sessions: () => http.get("/dex/sessions").then((r) => r.data),
    history: (sid) => http.get(`/dex/sessions/${sid}`).then((r) => r.data),
    deleteSession: (sid) => http.delete(`/dex/sessions/${sid}`).then((r) => r.data),
  },
  ce: {
    snapshot: () => http.get(`/cloud-economics`).then((r) => r.data),
    costExpense: () => http.get(`/cloud-economics/cost-expense`).then((r) => r.data),
  },
  exec: {
    overview: (months = 6) => http.get(`/exec/overview`, { params: { months } }).then((r) => r.data),
    anomalies: (months = 6) => http.get(`/exec/anomalies`, { params: { months } }).then((r) => r.data),
  },
  close: {
    review: (period) => http.get(`/close/review`, { params: { period } }).then((r) => r.data),
    decide: (body) => http.put(`/close/decision`, body).then((r) => r.data),
  },
  alloc: {    months: () => http.get(`/alloc/months`).then((r) => r.data),
    report: (month) => http.get(`/alloc/report`, { params: { month } }).then((r) => r.data),
    drill: (app, month) => http.get(`/alloc/drill`, { params: { app, month } }).then((r) => r.data),
    waterfall: (month) => http.get(`/alloc/waterfall`, { params: { month } }).then((r) => r.data),
    rules: () => http.get(`/alloc/rules`).then((r) => r.data),
    saveBundle: (b) => http.put(`/alloc/rules/bundles`, b).then((r) => r.data),
    saveShared: (s) => http.put(`/alloc/rules/shared`, s).then((r) => r.data),
    addFunding: (f) => http.post(`/alloc/rules/funding`, f).then((r) => r.data),
    exportBlob: (report, month, fmt) => http.get(`/alloc/export/${report}`, { params: { month, fmt }, responseType: "blob" }).then((r) => r.data),
    drivers: () => http.get(`/alloc/drivers`).then((r) => r.data),
    saveDriver: (d) => http.post(`/alloc/drivers`, d).then((r) => r.data),
    deleteDriver: (id) => http.delete(`/alloc/drivers/${id}`).then((r) => r.data),
    schedule: () => http.get(`/alloc/schedule`).then((r) => r.data),
    saveSchedule: (s) => http.put(`/alloc/schedule`, s).then((r) => r.data),
    sendNow: () => http.post(`/alloc/schedule/send-now`).then((r) => r.data),
  },
  econ: {
    widgets: () => http.get(`/econ/widgets`).then((r) => r.data),
    widgetData: () => http.get(`/econ/widget-data`).then((r) => r.data),
    dashboards: () => http.get(`/econ/dashboards`).then((r) => r.data),
    createDashboard: (payload) => http.post(`/econ/dashboards`, payload).then((r) => r.data),
    updateDashboard: (id, payload) => http.put(`/econ/dashboards/${id}`, payload).then((r) => r.data),
    deleteDashboard: (id) => http.delete(`/econ/dashboards/${id}`).then((r) => r.data),
  },
  llm: {
    list: () => http.get(`/llm/config`).then((r) => r.data),
    save: (id, api_key, selected_model) =>
      http.post(`/llm/config/${id}`, { api_key, selected_model }).then((r) => r.data),
    remove: (id) => http.delete(`/llm/config/${id}`).then((r) => r.data),
  },
  kube: {
    clusters: () => http.get(`/kube/clusters`).then((r) => r.data),
    fleet: () => http.get(`/kube/fleet`).then((r) => r.data),
    overview: (cid) => http.get(`/kube/cluster/${cid}/overview`).then((r) => r.data),
    costs: (cid, group_by) => http.get(`/kube/cluster/${cid}/costs`, { params: { group_by } }).then((r) => r.data),
    rightsizing: (cid, percentile, namespace) =>
      http.get(`/kube/cluster/${cid}/rightsizing`, { params: { percentile, namespace } }).then((r) => r.data),
    workload: (cid, id, percentile) =>
      http.get(`/kube/cluster/${cid}/workload`, { params: { id, percentile } }).then((r) => r.data),
    automation: (cid) => http.get(`/kube/cluster/${cid}/automation`).then((r) => r.data),
    saveRule: (rule) => http.put(`/kube/rules`, rule).then((r) => r.data),
    deleteRule: (id) => http.delete(`/kube/rules/${id}`).then((r) => r.data),
    savings: (cid) => http.get(`/kube/cluster/${cid}/savings`).then((r) => r.data),
    log: (cid, severity, limit = 60) =>
      http.get(`/kube/cluster/${cid}/log`, { params: { severity, limit } }).then((r) => r.data),
    apply: (payload) => http.post(`/kube/apply`, payload).then((r) => r.data),
    changesets: (cluster_id) => http.get(`/kube/changesets`, { params: { cluster_id } }).then((r) => r.data),
    ackRollback: (id) => http.post(`/kube/rollbacks/${id}/ack`).then((r) => r.data),
    connections: () => http.get(`/kube/connections`).then((r) => r.data),
    saveConnection: (c) => http.put(`/kube/connections`, c).then((r) => r.data),
    deleteConnection: (id) => http.delete(`/kube/connections/${id}`).then((r) => r.data),
    validateConnection: (id) => http.post(`/kube/connections/${id}/validate`).then((r) => r.data),
    activateConnection: (id) => http.post(`/kube/connections/${id}/activate`).then((r) => r.data),
    pauseConnection: (id) => http.post(`/kube/connections/${id}/pause`).then((r) => r.data),
    agentManifest: (id) => http.get(`/kube/connections/${id}/agent-manifest`).then((r) => r.data),
    gitopsConfig: () => http.get(`/kube/gitops/config`).then((r) => r.data),
    saveGitops: (cfg) => http.put(`/kube/gitops/config`, cfg).then((r) => r.data),
    verifyGitops: () => http.post(`/kube/gitops/verify`).then((r) => r.data),
    pullRequests: (cluster) => http.get(`/kube/gitops/pull-requests`, { params: { cluster } }).then((r) => r.data),
    alerts: () => http.get(`/kube/alerts`).then((r) => r.data),
    saveAlerts: (s) => http.put(`/kube/alerts`, s).then((r) => r.data),
    testAlert: () => http.post(`/kube/alerts/test`).then((r) => r.data),
    runAlertChecks: () => http.post(`/kube/alerts/run-checks`).then((r) => r.data),
    sendDigest: () => http.post(`/kube/alerts/digest`).then((r) => r.data),
  },
  chargeback: {
    overview: () => http.get(`/chargeback/overview`).then((r) => r.data),
    invoices: () => http.get(`/chargeback/invoices`).then((r) => r.data),
    adjustments: () => http.get(`/chargeback/adjustments`).then((r) => r.data),
    final: () => http.get(`/chargeback/final`).then((r) => r.data),
    athenaViews: () => http.get(`/chargeback/athena-views`).then((r) => r.data),
  },
};

/* ── formatters ─────────────────────────────────────────────────────────── */

export const fmtUSD = (n, opts = {}) => {
  const v = Number(n || 0);
  if (opts.compact && Math.abs(v) >= 1000) {
    return `$${(v / 1000).toFixed(1)}k`;
  }
  // Auto-precision for small values so $0.62 doesn't render as $1.
  const av = Math.abs(v);
  let minD = 0, maxD = 0;
  if (av > 0 && av < 0.01)   { minD = 4; maxD = 6; }
  else if (av < 1)           { minD = 2; maxD = 4; }
  else if (av < 100)         { minD = 2; maxD = 2; }
  if (opts.minimumFractionDigits != null) minD = opts.minimumFractionDigits;
  if (opts.maximumFractionDigits != null) maxD = opts.maximumFractionDigits;
  return v.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: minD,
    maximumFractionDigits: maxD,
  });
};

export const fmtNumber = (n) => Number(n || 0).toLocaleString("en-US");

export const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export const fmtRelative = (iso) => {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

export const fmtCountdown = (seconds) => {
  if (seconds === null || seconds === undefined) return "—";
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
};
